import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ArrowRight, Clock, Calendar, BookOpen, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { departments, programmes, images, applicationFormUrl } from '../data/mock';

const getDeptById = (deptId) => {
  for (let i = 0; i < departments.length; i++) {
    if (departments[i].id === deptId) return departments[i];
  }
  return null;
};

const Programmes = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [ntaFilter, setNtaFilter] = useState('all');

  const filteredProgrammes = useMemo(() => {
    return programmes.filter((programme) => {
      const matchesSearch =
        programme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        programme.overview.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDepartment =
        departmentFilter === 'all' || programme.departmentId === departmentFilter;
      const matchesIntake =
        intakeFilter === 'all' || programme.intakes.includes(intakeFilter);
      const matchesNta =
        ntaFilter === 'all' || programme.ntaLevels.includes(parseInt(ntaFilter));

      return matchesSearch && matchesDepartment && matchesIntake && matchesNta;
    });
  }, [searchQuery, departmentFilter, intakeFilter, ntaFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setDepartmentFilter('all');
    setIntakeFilter('all');
    setNtaFilter('all');
  };

  const hasActiveFilters =
    searchQuery || departmentFilter !== 'all' || intakeFilter !== 'all' || ntaFilter !== 'all';

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.scienceLab})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Our Programmes
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Explore our comprehensive range of NACTE-accredited programmes at NTA Level 4-6.
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-[#f8fafc] border-b border-[#e2e8f0] sticky top-[72px] z-40">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
              <Input
                type="text"
                placeholder="Search programmes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-[#e2e8f0] focus:border-[#2563eb] focus:ring-[#2563eb]"
              />
            </div>

            {/* Department Filter */}
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full lg:w-[200px] bg-white">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.shortName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Intake Filter */}
            <Select value={intakeFilter} onValueChange={setIntakeFilter}>
              <SelectTrigger className="w-full lg:w-[180px] bg-white">
                <SelectValue placeholder="Intake" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Intakes</SelectItem>
                <SelectItem value="March">March Intake</SelectItem>
                <SelectItem value="September">September Intake</SelectItem>
              </SelectContent>
            </Select>

            {/* NTA Level Filter */}
            <Select value={ntaFilter} onValueChange={setNtaFilter}>
              <SelectTrigger className="w-full lg:w-[150px] bg-white">
                <SelectValue placeholder="NTA Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="4">NTA Level 4</SelectItem>
                <SelectItem value="5">NTA Level 5</SelectItem>
                <SelectItem value="6">NTA Level 6</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-[#475569] hover:text-[#1e3a5f]"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Programmes List */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Results count */}
          <div className="mb-8 flex items-center justify-between">
            <p className="text-[#475569]">
              Showing <span className="font-semibold text-[#1e3a5f]">{filteredProgrammes.length}</span>{' '}
              of {programmes.length} programmes
            </p>
          </div>

          {filteredProgrammes.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-[#1e3a5f] mb-2">
                No programmes found
              </h3>
              <p className="text-[#475569] mb-4">
                Try adjusting your filters or search query.
              </p>
              <Button onClick={clearFilters} variant="outline">
                Clear All Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProgrammes.map((programme) => {
                const dept = getDeptById(programme.departmentId);
                return (
                  <Link key={programme.id} to={`/programmes/${programme.id}`}>
                    <Card className="h-full card-hover border-none shadow-md hover:shadow-xl cursor-pointer">
                      <CardHeader className="pb-2">
                        <Badge className="w-fit bg-[#1e3a5f] text-white text-xs mb-2">
                          {dept?.shortName}
                        </Badge>
                        <CardTitle className="font-display text-[#1e3a5f] text-xl leading-tight">
                          {programme.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-[#475569] text-sm line-clamp-2 mb-4">
                          {programme.overview}
                        </p>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-[#475569]">
                            <BookOpen className="w-4 h-4 text-[#d4a855]" />
                            <span>NTA Level {programme.ntaLevels.join('-')}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#475569]">
                            <Clock className="w-4 h-4 text-[#d4a855]" />
                            <span>{programme.duration}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#475569]">
                            <Calendar className="w-4 h-4 text-[#d4a855]" />
                            <span>{programme.intakes.join(' & ')} Intake</span>
                          </div>
                        </div>
                        <div className="flex items-center text-[#2563eb] font-medium text-sm group">
                          View Details
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-12 bg-[#fef3c7]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-xl font-semibold text-[#92400e] mb-2">
                Important Notice
              </h3>
              <p className="text-[#92400e]/80">
                Health & Allied Sciences programmes are available only during the <strong>September intake</strong>.
                All other departments offer both March and September intakes.
              </p>
            </div>
            <Link to="/admissions">
              <Button className="bg-[#92400e] hover:bg-[#78350f] text-white whitespace-nowrap">
                View Admission Info
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Take the first step towards your future career. Apply now or request more information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold px-8">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8">
                Request Information
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Programmes;
